import { useState, type ReactNode } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "src/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "src/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { cn } from "src/lib/utils";
import type { ApiPayload, PaginatedList } from "src/types/api";

/** What `mapFN` turns an API row into: the label to match on, the value to emit, the row itself. */
export interface ComboOption<TInfo> {
	label?: string;
	value: string;
	info: TInfo;
}

interface ComboSearchProps<TRow, TInfo> {
	/** Currently selected `value`. */
	value?: string;
	/** Receives the option's `value`, plus the row behind it. Selecting the current value clears it. */
	onChange?: (value: string, info: TInfo) => void;
	/** Cache key suffix — the search term is appended. */
	queryKey: string;
	/** List endpoint, called with the typed search term. */
	queryFN: (params: { search: string }) => Promise<ApiPayload<PaginatedList<TRow>>>;
	mapFN: (option: TRow) => ComboOption<TInfo>;
	/** Renders one row in the dropdown. Falls back to the option's label. */
	optionRenderer?: (info: TInfo) => ReactNode;
	placeholder?: string;
	searchPlaceholder?: string;
	notFoundMessage?: string;
	className?: string;
	inputClassName?: string;
	optionsContainerClassName?: string;
}

/**
 * Async searchable select. Filtering happens server-side via `queryFN`; the local
 * `filter` only keeps cmdk from re-filtering what the server already narrowed.
 */
const ComboSearch = <TRow, TInfo>({
	value,
	onChange,
	queryKey,
	queryFN,
	mapFN,
	optionRenderer,
	placeholder = "Select option",
	searchPlaceholder,
	notFoundMessage = "No results found.",
	className,
	inputClassName,
	optionsContainerClassName,
}: ComboSearchProps<TRow, TInfo>) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const { data } = useQuery({
		queryKey: [`combo-${queryKey}`, search],
		queryFn: () => queryFN({ search }),
	});

	const options = (data?.data?.data ?? []).map(mapFN);
	const selected = options.find((option) => option.value === value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn(
						"tw-h-11 tw-w-full tw-justify-between tw-gap-3 tw-rounded-[10px] tw-border-border tw-bg-white tw-px-3 tw-text-[14px] tw-font-normal",
						selected ? "tw-text-text" : "tw-text-text-soft",
						className
					)}
				>
					<span className="tw-truncate">{selected?.label ?? placeholder}</span>
					<ChevronsUpDown className="tw-ml-auto tw-size-4 tw-shrink-0 tw-opacity-50" />
				</Button>
			</PopoverTrigger>

			<PopoverContent align="start" className={cn("tw-w-[var(--radix-popover-trigger-width)] tw-p-0", optionsContainerClassName)} removePortal>
				<Command
					filter={(_option, term, keywords) => {
						if (!term) return 0;
						return Number(keywords?.some((keyword) => keyword?.toLowerCase().includes(term.toLowerCase())));
					}}
					className="tw-w-full"
				>
					<CommandInput
						className={inputClassName}
						placeholder={searchPlaceholder || "Search..."}
						value={search}
						onValueChange={setSearch}
					/>

					<CommandList>
						<CommandEmpty>{notFoundMessage}</CommandEmpty>
						<CommandGroup className="tw-overflow-y-auto">
							{options.map((option) => (
								<CommandItem
									keywords={[option.label ?? ""]}
									key={option.value}
									value={option.value}
									className="tw-cursor-pointer tw-justify-between tw-capitalize"
									onSelect={(currentValue) => {
										onChange?.(currentValue === value ? "" : currentValue, option.info);
										setOpen(false);
									}}
								>
									{optionRenderer ? optionRenderer(option.info) : option.label}
									<Check className={cn("tw-size-4", value === option.value ? "tw-opacity-100" : "tw-opacity-0")} />
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

export default ComboSearch;
